import { Student, StudentStatus } from './student.model';
import { RegistrationStatus, StatusConfig } from './registration-status.model';

/**
 * UI-specific student status configurations, labels, and helper functions
 * Separated from student.model.ts to keep core data models clean
 */

export const StudentStatusConfigs: Record<StudentStatus, StatusConfig> = {
  no_registration: {
    type: 'info',
    label: $localize`:@@lib.student-status.no-registration:Keine Anmeldung`,
    action: $localize`:@@lib.student-status.no-registration-action:Es liegt noch keine Anmeldung vor.`,
    styles: { background: '#6c757d', color: '#fff' },
  },

  registration_new: {
    type: 'info',
    label: $localize`:@@lib.student-status.registration-new:Neue Anmeldung`,
    action: $localize`:@@lib.student-status.registration-new-action:Die Anmeldung ist eingegangen und wird geprüft.`,
    styles: { background: 'var(--theme-accent)', color: '#fff' },
  },

  registration_custom_start_date_pending: {
    type: 'info',
    label: $localize`:@@lib.student-status.registration-custom-start-date-pending:Startdatum wird geprüft`,
    action: $localize`:@@lib.student-status.registration-custom-start-date-pending-action:Die Anmeldung ist eingegangen und ihr gewünschtes Startdatum wird geprüft.`,
    styles: { background: 'var(--theme-accent)', color: '#fff' },
  },

  registration_waitlist: {
    type: 'warning',
    label: $localize`:@@lib.student-status.registration-waitlist:Warteliste`,
    action: $localize`:@@lib.student-status.registration-waitlist-action:Anmeldung befindet sich auf der Warteliste.`,
    styles: { background: 'var(--theme-orange)', color: '#fff' },
  },

  registration_contract_finalization: {
    type: 'info',
    label: $localize`:@@lib.student-status.registration-contract-finalization:Vertragsphase`,
    action: $localize`:@@lib.student-status.registration-contract-finalization-action:Anmeldung angenommen, Vertragsdaten werden finalisiert.`,
    styles: { background: 'var(--theme-accent)', color: '#fff' },
  },

  registration_completed: {
    type: 'success',
    label: $localize`:@@lib.student-status.registration-completed:Vertrag ausstehend`,
    action: $localize`:@@lib.student-status.registration-completed-action:Bitte Vertrag abschließen`,
    styles: { background: 'var(--theme-success)', color: '#fff' },
  },

  registration_cancelled: {
    type: 'error',
    label: $localize`:@@lib.student-status.registration-cancelled:Anmeldung gekündigt`,
    action: $localize`:@@lib.student-status.registration-cancelled-action:Die Anmeldung wurde gekündigt.`,
    styles: { background: 'var(--theme-error)', color: '#fff' },
  },

  registration_deleted: {
    type: 'error',
    label: $localize`:@@lib.student-status.registration-deleted:Anmeldung gelöscht`,
    action: $localize`:@@lib.student-status.registration-deleted-action:Die Anmeldung wurde gelöscht.`,
    styles: { background: 'var(--theme-warn)', color: '#fff' },
  },

  registration_cancellation_requested: {
    type: 'warning',
    label: $localize`:@@lib.student-status.registration-cancellation-requested:Kündigung beantragt`,
    action: $localize`:@@lib.student-status.registration-cancellation-requested-action:Eine Kündigung der Anmeldung wurde beantragt.`,
    styles: { background: 'var(--theme-orange)', color: '#fff' },
  },

  contract_active: {
    type: 'success',
    label: $localize`:@@lib.student-status.contract-active:Vertrag aktiv`,
    action: $localize`:@@lib.student-status.contract-active-action:Der Vertrag ist gültig ab`,
    styles: { background: 'var(--theme-success)', color: '#fff' },
  },

  contract_renewal_pending: {
    type: 'warning',
    label: $localize`:@@lib.student-status.contract-renewal-pending:Verlängerung ausstehend`,
    action: $localize`:@@lib.student-status.contract-renewal-pending-action:Eine Vertragsverlängerung steht aus.`,
    styles: { background: 'var(--theme-yellow)', color: '#fff' },
  },

  contract_terminated: {
    type: 'error',
    label: $localize`:@@lib.student-status.contract-terminated:Vertrag beendet`,
    action: $localize`:@@lib.student-status.contract-terminated-action:Der Vertrag wurde beendet.`,
    styles: { background: 'var(--theme-warn)', color: '#fff' },
  },

  contract_cancelled: {
    type: 'error',
    label: $localize`:@@lib.student-status.contract-cancelled:Vertrag gekündigt`,
    action: $localize`:@@lib.student-status.contract-cancelled-action:Der Vertrag wurde gekündigt.`,
    styles: { background: 'var(--theme-error)', color: '#fff' },
  },

  contract_cancellation_requested: {
    type: 'warning',
    label: $localize`:@@lib.student-status.contract-cancellation-requested:Vertragskündigung beantragt`,
    action: $localize`:@@lib.student-status.contract-cancellation-requested-action:Eine Vertragskündigung wurde beantragt.`,
    styles: { background: 'var(--theme-orange)', color: '#fff' },
  },

  contract_cancellation_approved: {
    type: 'warning',
    label: $localize`:@@lib.student-status.contract-cancellation-approved:Vertragskündigung genehmigt`,
    action: $localize`:@@lib.student-status.contract-cancellation-approved-action:Vertragskündigung zum`,
    styles: { background: 'var(--theme-orange)', color: '#fff' },
  },
};

/**
 * Computes the comprehensive student status based on registration and contract states
 */
export function getStudentStatus(student: Student): StudentStatus {
  // No registration case
  if (!student.registration) {
    return 'no_registration';
  }

  // If there's a contract, prioritize contract status
  if (student.contract) {
    switch (student.contract.status) {
      case 'active':
        return 'contract_active';
      case 'renewalPending':
        return 'contract_renewal_pending';
      case 'terminated':
        return 'contract_terminated';
      case 'cancelled':
        return 'contract_cancelled';
      case 'cancellationRequested':
        return 'contract_cancellation_requested';
      case 'cancellationApproved':
        return 'contract_cancellation_approved';
      default:
        return 'contract_active'; // fallback
    }
  }

  // No contract, use registration status
  switch (student.registration.status) {
    case 'new':
      return 'registration_new';
    case 'customStartDatePending':
      return 'registration_custom_start_date_pending';
    case 'waitlist':
      return 'registration_waitlist';
    case 'contractFinalization':
      return 'registration_contract_finalization';
    case 'completed':
      return 'registration_completed';
    case 'cancelled':
      return 'registration_cancelled';
    case 'deleted':
      return 'registration_deleted';
    case 'cancellationRequested':
      return 'registration_cancellation_requested';
    default:
      return 'registration_new'; // fallback
  }
}

/**
 * Status, den ein Kind nach einem Wechsel des Anmeldestatus trägt.
 *
 * Läuft bewusst über {@link getStudentStatus} statt über eine direkte
 * Zuordnung `RegistrationStatus → StudentStatus`: ein bestehender Vertrag
 * gewinnt weiterhin, ein Kind mit aktivem Vertrag darf also nie auf einen
 * `registration_*`-Status zurückfallen.
 *
 * Wird beim Schreiben von `registration.status` gebraucht, damit das
 * denormalisierte Feld `currentStatus` mitzieht — es ist die Quelle für das
 * Stammdatenblatt-PDF, den Excel-Export und den Billing-Snapshot (#1001).
 */
export function getStudentStatusAfterRegistrationChange(
  student: Student,
  newStatus: RegistrationStatus
): StudentStatus {
  if (!student.registration) return 'no_registration';
  return getStudentStatus({
    ...student,
    registration: { ...student.registration, status: newStatus },
  });
}

/**
 * Groups student statuses for filtering and display
 */
export const StudentStatusGroups = {
  noRegistration: ['no_registration'],
  registrationPending: [
    'registration_new',
    'registration_custom_start_date_pending',
    'registration_waitlist',
    'registration_contract_finalization',
  ],
  registrationCompleted: ['registration_completed'],
  registrationCancelled: [
    'registration_cancelled',
    'registration_deleted',
    'registration_cancellation_requested',
  ],
  contractActive: ['contract_active'],
  contractPending: ['contract_renewal_pending'],
  contractTerminated: [
    'contract_terminated',
    'contract_cancelled',
    'contract_cancellation_requested',
    'contract_cancellation_approved',
  ],
} as const;

export const StudentStatusOptions = [
  { value: '', label: $localize`:@@lib.student-status.all:Alle` },
  ...Object.entries(StudentStatusConfigs).map(([value, config]) => ({
    value,
    label: config.label,
  })),
];
